import { RenderStateCamera } from "@novorender/api";
import { quat, vec2, vec3 } from "gl-matrix";

import { log } from "utils/log";
import { clamp, degToRad } from "utils/math";

import { createSheetProjection, isCameraOnSheet, projectToSheet, sheetEdgeDistance } from "./sheetProjection";
import { DrawingAB, DrawingPlacement, DrawingSpacePosition, Level, ModelAB, NavigationPin } from "./types";

// `useMiniMapData` is intentionally NOT re-exported from this module. It
// lives in `./useMiniMapData` so that pure-utility consumers (and their
// tests) of `miniMapUtils` do not transitively pull in `procoreApi` — which
// would create a circular import via `procoreApi → procoreClient → app/store
// → features/miniMap`. Component consumers should import it directly:
// `import { useMiniMapData } from "./useMiniMapData"`.
//
// `fetchDrawingData` does not depend on `procoreApi` and is re-exported here
// for source-compatibility with existing consumers.
export { fetchDrawingData } from "./fetchDrawingData";
export { createSheetProjection, isCameraOnSheet, sheetEdgeDistance } from "./sheetProjection";

const l = log.miniMap.utils;

export const EYE_ELEVATION_OFFSET = 1.6764; // 5 ft 6 in (average eye level) in meters

/**
 * The position indicator is a 24×24 SVG drawn centered on the given position.
 * Insetting a pinned indicator by this half-size keeps it fully visible.
 */
export const POSITION_INDICATOR_HALF_SIZE = 12;

export function drawPositionIndicator(ctx: CanvasRenderingContext2D, posX: number, posY: number, rotation: number) {
    // Center of the 24x24 SVG indicator
    const indicatorCenterX = POSITION_INDICATOR_HALF_SIZE;
    const indicatorCenterY = POSITION_INDICATOR_HALF_SIZE;

    const rotationRad = degToRad(rotation);

    ctx.save();

    ctx.translate(posX, posY);
    ctx.rotate(rotationRad);
    ctx.translate(-indicatorCenterX, -indicatorCenterY);

    ctx.shadowColor = "rgba(0, 0, 0, 0.48)";
    ctx.shadowBlur = 2.5;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;

    const fillPath = new Path2D("M5 20L11.5625 4L19 20L11.5625 18.2222L5 20Z");
    ctx.fillStyle = "#2066DF";
    ctx.fill(fillPath);

    const strokePath = new Path2D(
        "M11.54 3C11.9371 2.99108 12.3023 3.218 12.4697 3.57812L19.9072 19.5781C20.0689 19.9259 20.0171 20.3359 19.7744 20.6328C19.5318 20.9298 19.1406 21.0618 18.7676 20.9727L11.5791 19.2529L5.26172 20.9648C4.89439 21.0644 4.50186 20.9474 4.25 20.6621C3.99815 20.3767 3.93076 19.9723 4.0752 19.6201L10.6377 3.62012L10.7031 3.48828C10.8774 3.19544 11.1926 3.00798 11.54 3Z",
    );
    ctx.strokeStyle = "white";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.stroke(strokePath);

    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    ctx.restore();
}

export function drawLargeMapPositionIndicator(
    ctx: CanvasRenderingContext2D,
    posX: number,
    posY: number,
    rotation: number,
    indicatorScale = 1,
    indicatorOpacity = 1,
) {
    const indicatorCenterX = 23.35;
    const indicatorCenterY = 36.48;

    const rotationRad = degToRad(rotation);

    ctx.save();
    ctx.globalAlpha = indicatorOpacity;

    ctx.translate(posX, posY);
    ctx.scale(indicatorScale, indicatorScale);
    ctx.rotate(rotationRad);
    ctx.translate(-indicatorCenterX, -indicatorCenterY);

    // Draw gradient cone (top triangle)
    const coneGradient = ctx.createLinearGradient(24.4125, 0.984, 24.4125, 29.69);
    coneGradient.addColorStop(0, "rgba(112, 152, 215, 0)");
    coneGradient.addColorStop(0.665, "rgba(112, 152, 215, 0.5)");
    coneGradient.addColorStop(1, "rgba(112, 152, 215, 1)");

    const conePath = new Path2D(
        "M31.4633 31.4229C29.2379 30.7216 26.8695 30.3428 24.4125 30.3428C21.2998 30.3428 18.329 30.9499 15.6117 32.0518L0.909546 0L45.8793 0L31.4633 31.4229Z",
    );
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = coneGradient;
    ctx.fill(conePath);
    ctx.globalAlpha = 1;

    // Draw white ellipse with shadow
    ctx.shadowColor = "rgba(13, 41, 89, 0.25)";
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;

    ctx.beginPath();
    ctx.ellipse(23.3519, 36.4843, 15.3519, 15.115, 0, 0, Math.PI * 2);
    ctx.fillStyle = "white";
    ctx.fill();

    // Draw ellipse stroke
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    ctx.beginPath();
    ctx.ellipse(23.3519, 36.4843, 15.3519, 15.115, 0, 0, Math.PI * 2);
    ctx.strokeStyle = "#EEF0F1";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Draw blue circle
    const blueCirclePath = new Path2D(
        "M23.3376 49.3458C30.5444 49.3458 36.3867 43.5937 36.3867 36.4981C36.3867 29.4025 30.5444 23.6504 23.3376 23.6504C16.1307 23.6504 10.2885 29.4025 10.2885 36.4981C10.2885 43.5937 16.1307 49.3458 23.3376 49.3458Z",
    );
    ctx.fillStyle = "#2066DF";
    ctx.fill(blueCirclePath);

    // Draw white arrow inside blue circle
    const arrowPath = new Path2D(
        "M23.7374 31.613L27.9536 39.2232C28.0647 39.4239 27.9896 39.6753 27.7858 39.7847C27.6932 39.8345 27.5849 39.8481 27.4826 39.8229L23.4704 38.8353C23.4034 38.8189 23.3334 38.8189 23.2665 38.8353L19.2542 39.8229C19.029 39.8784 18.8008 39.7435 18.7445 39.5218C18.7189 39.421 18.7327 39.3144 18.7833 39.2232L22.9994 31.613C23.1106 31.4123 23.3659 31.3384 23.5697 31.4478C23.6406 31.4859 23.6988 31.5432 23.7374 31.613Z",
    );
    ctx.fillStyle = "white";
    ctx.fill(arrowPath);

    ctx.restore();
}

export function modelToDrawingSpace(
    modelAB: ModelAB,
    drawingAB: DrawingAB,
    camera: RenderStateCamera,
): DrawingSpacePosition {
    const projection = createSheetProjection(modelAB, drawingAB);
    const { x, y } = projectToSheet(projection, camera.position[0], camera.position[1]);

    const cameraYaw = getYawFromQuaternion(camera.rotation);
    let drawingRotation = normalizeAngle(-(cameraYaw + projection.rotationOffset) * (180 / Math.PI));

    if (!Number.isFinite(drawingRotation)) {
        drawingRotation = 0;
    }

    return {
        position: vec2.fromValues(x, y),
        rotation: drawingRotation,
    };
}

export type ModelSpacePosition = {
    position: vec3;
    rotation: quat;
};

export function drawingToModelSpace(
    drawingPosition: DrawingSpacePosition,
    modelAB: ModelAB,
    drawingAB: DrawingAB,
    elevation: number,
): ModelSpacePosition {
    const { pointA: modelA, pointB: modelB } = modelAB;
    const { pointA: drawingA, pointB: drawingB, sheetDimensions } = drawingAB;

    const sheetWidth = sheetDimensions[0];
    const sheetHeight = sheetDimensions[1];

    const drawingX = drawingPosition.position[0] * sheetWidth;
    const drawingY = sheetHeight * (1 - drawingPosition.position[1]);

    const modelA2D = vec2.fromValues(modelA[0], modelA[1]);
    const modelB2D = vec2.fromValues(modelB[0], modelB[1]);

    const modelVec = vec2.create();
    vec2.subtract(modelVec, modelB2D, modelA2D);

    const drawingVec = vec2.create();
    vec2.subtract(drawingVec, drawingB, drawingA);

    const modelDist = vec2.length(modelVec);
    const drawingDist = vec2.length(drawingVec);

    if (drawingDist < 0.0001 || !drawingDist) {
        l.warn(
            "drawingToModelSpace: drawingDist is too small or not exist, the data from server is not valid",
            drawingDist,
        );
    }

    const inverseScale = modelDist / drawingDist;

    const modelAngle = Math.atan2(modelVec[1], modelVec[0]);
    const drawingAngle = Math.atan2(drawingVec[1], drawingVec[0]);
    const rotationOffset = drawingAngle - modelAngle;

    const drawingRelative = vec2.fromValues(drawingX - drawingA[0], drawingY - drawingA[1]);

    const scaledPos = vec2.create();
    vec2.scale(scaledPos, drawingRelative, inverseScale);

    const cosR = Math.cos(-rotationOffset);
    const sinR = Math.sin(-rotationOffset);
    const modelRelative = vec2.create();
    modelRelative[0] = scaledPos[0] * cosR - scaledPos[1] * sinR;
    modelRelative[1] = scaledPos[0] * sinR + scaledPos[1] * cosR;

    const modelX = modelRelative[0] + modelA[0];
    const modelY = modelRelative[1] + modelA[1];

    // Calculate target yaw in model space
    const drawingRotationRad = degToRad(drawingPosition.rotation);
    const targetYaw = -drawingRotationRad - rotationOffset;

    // Rz(targetYaw) * Rx(π/2): camera looks horizontally at the sheet's yaw angle.
    // Without the Rx(π/2) tilt, the camera would look straight down (identity forward is -Z).
    const modelRotation = levelForwardRotation(targetYaw);

    return {
        position: vec3.fromValues(modelX, modelY, elevation),
        rotation: modelRotation,
    };
}

// Rz(yaw) * Rx(π/2): yaw rotation around Z composed with 90° pitch around X.
// Produces a level, forward-facing camera at the given yaw angle.
// Expands to: (cos(yaw/2)/√2, sin(yaw/2)/√2, sin(yaw/2)/√2, cos(yaw/2)/√2)
function levelForwardRotation(yaw: number): quat {
    const halfYaw = yaw / 2;
    const r = Math.SQRT1_2; // sin(π/4) = cos(π/4)
    return quat.fromValues(Math.cos(halfYaw) * r, Math.sin(halfYaw) * r, Math.sin(halfYaw) * r, Math.cos(halfYaw) * r);
}

function getYawFromQuaternion(q: quat): number {
    const [x, y, z, w] = q;

    const siny_cosp = 2 * (w * z + x * y);
    const cosy_cosp = 1 - 2 * (y * y + z * z);
    return Math.atan2(siny_cosp, cosy_cosp);
}

function normalizeAngle(degrees: number): number {
    let normalized = degrees % 360;
    if (normalized < 0) {
        normalized += 360;
    }
    return normalized;
}

/**
 * Fraction of the drawing's longer side that should be visible in the mobile
 * mini map. `1` fits the whole drawing (web-like). Tunable with design (BM-12546).
 *
 * The previous `naturalPx * 0.2` scale showed ~11% of a typical 4k sheet, which
 * was too tight to identify the surrounding rooms. `0.3` keeps the crop centered
 * on the camera while revealing neighboring context on both phone and tablet.
 */
export const MOBILE_MINIMAP_VISIBLE_FRACTION = 0.3;

/**
 * Uniform scale that maps a drawing's natural pixel size onto a mini-map canvas
 * so `visibleFraction` of the longer side fills the canvas. Independent of sheet
 * resolution, unlike the old `natural * 0.2` scale which zoomed high-res sheets
 * much tighter than low-res ones.
 *
 * Returns 1 (no scaling) when any dimension or the fraction is non-positive.
 * The guard is defensive only — drawing and canvas dimensions are always
 * positive in practice.
 */
export function mobileMiniMapImageScale(
    naturalWidth: number,
    naturalHeight: number,
    canvasWidth: number,
    canvasHeight: number,
    visibleFraction: number = MOBILE_MINIMAP_VISIBLE_FRACTION,
): number {
    if (naturalWidth <= 0 || naturalHeight <= 0 || canvasWidth <= 0 || canvasHeight <= 0 || visibleFraction <= 0) {
        return 1;
    }
    const drawingLonger = Math.max(naturalWidth, naturalHeight);
    const canvasShorter = Math.min(canvasWidth, canvasHeight);
    return canvasShorter / (drawingLonger * visibleFraction);
}

export type MiniMapFollowLayout = {
    offsetX: number;
    offsetY: number;
    indicatorX: number;
    indicatorY: number;
};

/**
 * Camera-follow pan for a cropped mini map. Centers the camera when the
 * drawing can fill the canvas around it; otherwise stops at the drawing
 * edge, so empty canvas never appears on an axis the drawing overfills.
 * An axis smaller than the canvas is centered (letterboxed). The indicator
 * tracks the camera until it leaves the drawing, then sits on the nearest
 * edge — inset by `indicatorInset` from the canvas edges so a pinned
 * indicator stays fully visible instead of rendering half-clipped.
 */
export function clampMiniMapFollowLayout({
    scaledWidth,
    scaledHeight,
    canvasWidth,
    canvasHeight,
    posXPercent,
    posYPercent,
    indicatorInset = 0,
}: {
    scaledWidth: number;
    scaledHeight: number;
    canvasWidth: number;
    canvasHeight: number;
    posXPercent: number;
    posYPercent: number;
    indicatorInset?: number;
}): MiniMapFollowLayout {
    const offsetX = clampFollowOffset(canvasWidth / 2 - posXPercent * scaledWidth, scaledWidth, canvasWidth);
    const offsetY = clampFollowOffset(canvasHeight / 2 - posYPercent * scaledHeight, scaledHeight, canvasHeight);
    const insetX = Math.min(indicatorInset, canvasWidth / 2);
    const insetY = Math.min(indicatorInset, canvasHeight / 2);

    return {
        offsetX,
        offsetY,
        indicatorX: clamp(offsetX + clamp(posXPercent, 0, 1) * scaledWidth, insetX, canvasWidth - insetX),
        indicatorY: clamp(offsetY + clamp(posYPercent, 0, 1) * scaledHeight, insetY, canvasHeight - insetY),
    };
}

function clampFollowOffset(desiredOffset: number, scaledSize: number, canvasSize: number): number {
    if (scaledSize <= canvasSize) {
        return (canvasSize - scaledSize) / 2;
    }
    return clamp(desiredOffset, canvasSize - scaledSize, 0);
}

export const calculateImageFitBox = ({
    imageDimensions,
    canvasDimensions,
    scale = 1,
    panOffset = vec2.fromValues(0, 0),
    margin = { top: 0, right: 0, bottom: 0, left: 0 },
}: {
    imageDimensions: vec2;
    canvasDimensions: vec2;
    scale?: number;
    panOffset?: vec2;
    margin?: { top: number; right: number; bottom: number; left: number };
}) => {
    const [imageWidth, imageHeight] = imageDimensions;
    const [canvasWidth, canvasHeight] = canvasDimensions;

    // Calculate available area after margins
    const availableWidth = canvasWidth - margin.left - margin.right;
    const availableHeight = canvasHeight - margin.top - margin.bottom;

    // Calculate scale to fit image within available area while maintaining aspect ratio
    const fitScale = Math.min(availableWidth / imageWidth, availableHeight / imageHeight);
    const baseWidth = imageWidth * fitScale;
    const baseHeight = imageHeight * fitScale;

    // Apply zoom scale
    const targetWidth = baseWidth * scale;
    const targetHeight = baseHeight * scale;

    // Center the image within the available area, then apply pan offset
    const x = margin.left + (availableWidth - targetWidth) / 2 + panOffset[0];
    const y = margin.top + (availableHeight - targetHeight) / 2 + panOffset[1];

    return { width: targetWidth, height: targetHeight, x, y };
};

/**
 * Formats a `>` delimited location path for display:
 * - 1 segment: as-is ("1st Floor")
 * - 2 segments: "Parent > Child" ("6th Floor > First Floor")
 * - 3+ segments: truncated ("6th Floor >...> Mens Restroom")
 *
 */
export function formatLevelDisplayName(fullPath: string): { displayName: string; tooltip: string } {
    const segments = fullPath.split(">").map((s) => s.trim());
    const tooltip = segments.join(" > ");

    if (segments.length <= 1) {
        return { displayName: segments[0] || fullPath, tooltip };
    }

    if (segments.length === 2) {
        return { displayName: `${segments[0]} > ${segments[1]}`, tooltip };
    }

    return {
        displayName: `${segments[0]} >...> ${segments[segments.length - 1]}`,
        tooltip,
    };
}

// Tolerance band, as a fraction of the sheet, applied around the *current*
// sheet's edge before we consider the camera to have left it. A camera sitting
// on the seam between two adjacent areas (e.g. a level split into North and
// South) would otherwise flip between their sheets on every frame.
const SHEET_EXIT_MARGIN = 0.02;

/**
 * Of the elevations that cover the camera, the one the camera is standing on:
 * the highest at or below it, or — when the camera is below every calibrated
 * area — the lowest.
 */
function resolveElevation(covering: { placement: DrawingPlacement }[], cameraZ: number): number {
    let atOrBelow = Number.NEGATIVE_INFINITY;
    let lowest = Number.POSITIVE_INFINITY;

    for (const { placement } of covering) {
        if (placement.elevation <= cameraZ && placement.elevation > atOrBelow) {
            atOrBelow = placement.elevation;
        }
        if (placement.elevation < lowest) {
            lowest = placement.elevation;
        }
    }

    return Number.isFinite(atOrBelow) ? atOrBelow : lowest;
}

/**
 * Pick the sheet that matches where the camera actually is — both its elevation
 * and its position on the plan.
 *
 * Elevation alone is not enough once a level carries more than one calibrated
 * sheet, which `sheet_mappings` made possible: a level split into North and
 * South areas, or a project holding several unrelated buildings whose levels sit
 * at overlapping elevations. Selecting on elevation only would pin such a
 * project to whichever sheet happened to come first in the API response.
 *
 * The camera keeps the sheet it is on until it leaves that sheet's area, so a
 * manual choice survives while the user moves around within it; crossing into a
 * neighbouring area hands over to that area's sheet. When nothing covers the
 * camera at all we fall back to `findDrawingIdByElevation` so the mini map
 * always has something to show.
 */
export function findDrawingIdForCamera(
    camera: RenderStateCamera,
    levels: Level[],
    placements: DrawingPlacement[],
    currentDrawingId?: string | null,
): string | null {
    const cameraZ = camera.position[2];

    const covering = placements
        .map((placement) => ({ placement, depth: sheetEdgeDistance(placement.projection, camera) }))
        .filter(({ placement, depth }) =>
            placement.drawingId === currentDrawingId ? depth >= -SHEET_EXIT_MARGIN : depth >= 0,
        );

    if (covering.length === 0) {
        return findDrawingIdByElevation(cameraZ, levels, currentDrawingId);
    }

    const elevation = resolveElevation(covering, cameraZ);
    const candidates = covering.filter(({ placement }) => placement.elevation === elevation);

    // Checked against the elevation-filtered candidates rather than everything
    // covering the camera, so that moving between stacked floors still switches
    // sheets even though the floor below keeps covering the camera in plan view.
    if (currentDrawingId && candidates.some(({ placement }) => placement.drawingId === currentDrawingId)) {
        return currentDrawingId;
    }

    return candidates.reduce((best, candidate) => (candidate.depth > best.depth ? candidate : best)).placement
        .drawingId;
}

export type SheetResolution = {
    // The sheet to show, or `null` while a pinned fly-to holds the current one.
    drawingId: string | null;
    // Set once a pinned fly-to has landed (or its deadline passed), so the
    // caller can drop the pin and let automatic switching resume.
    releasePin: boolean;
};

/**
 * Resolve the sheet for a camera update, honouring an in-flight navigation pin.
 *
 * A fly-to started from the large map takes a second, and the camera streams
 * updates from its *old* position the whole way. Without the pin those updates
 * would switch away from the sheet the user just picked, and the arrival would
 * then land on whichever sheet covers the destination most — not necessarily
 * theirs. The pin holds their choice until the camera is inside it.
 */
export function resolveSheetForCamera({
    camera,
    levels,
    placements,
    currentDrawingId,
    navigationPin,
    now = Date.now(),
}: {
    camera: RenderStateCamera;
    levels: Level[];
    placements: DrawingPlacement[];
    currentDrawingId?: string | null;
    navigationPin?: NavigationPin | null;
    now?: number;
}): SheetResolution {
    if (navigationPin) {
        const pinned = placements.find((placement) => placement.drawingId === navigationPin.drawingId);
        const landed = pinned ? isCameraOnSheet(pinned.projection, camera) : false;
        return { drawingId: null, releasePin: landed || now > navigationPin.expiresAt };
    }

    return {
        drawingId: findDrawingIdForCamera(camera, levels, placements, currentDrawingId),
        releasePin: false,
    };
}

/**
 * Elevation-only sheet lookup. Used as the fallback when no sheet's calibrated
 * area covers the camera — see `findDrawingIdForCamera`, which should be
 * preferred anywhere placement data is available.
 */
export function findDrawingIdByElevation(
    cameraZ: number,
    levels: Level[],
    currentDrawingId?: string | null,
): string | null {
    if (levels.length === 0) {
        return null;
    }

    let targetLevel = levels[0];

    if (cameraZ > levels[0].elevation) {
        for (let i = levels.length - 1; i >= 0; i--) {
            if (cameraZ >= levels[i].elevation) {
                targetLevel = levels[i];
                break;
            }
        }
    }

    if (targetLevel.drawingIds.length === 0) {
        return null;
    }

    if (currentDrawingId && targetLevel.drawingIds.includes(currentDrawingId)) {
        return currentDrawingId;
    }

    return targetLevel.drawingIds[0];
}
